import { useEffect, useRef } from 'react';
import { useUiStore, type Theme } from '@/store/useUiStore';
import { sampleSaudiPoints, SAUDI_POLYGON, type MapPoint } from './saudiPolygon';

/* ---------- palettes (plan §2.2, §6) ---------- */
interface Palette {
  mapDot: [number, number, number];
  particleA: [number, number, number];
  particleB: [number, number, number];
  arc: [number, number, number];
  glow: [number, number, number];
  terrain: [number, number, number];
}
const PALETTES: Record<Theme, Palette> = {
  dark: {
    mapDot: [111, 207, 178], // mint-400
    particleA: [224, 221, 214], // cream-100
    particleB: [196, 106, 140], // rose-glow
    arc: [196, 106, 140], // rose-glow
    glow: [196, 106, 140], // rose skyline glow
    terrain: [111, 207, 178], // mint
  },
  light: {
    mapDot: [93, 41, 63], // maroon-700
    particleA: [170, 122, 144], // mauve-400
    particleB: [122, 59, 85], // maroon-500
    arc: [93, 41, 63],
    glow: [196, 106, 140],
    terrain: [93, 41, 63],
  },
};

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  hue: 0 | 1; // particleA | particleB
  a: number;
}
interface Ripple {
  x: number;
  y: number;
  t: number; // 0..1 life
}

const DPR_MAX = 1.5;

export default function ValueField() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // transient store reads (no React re-render — plan §6)
  const themeRef = useRef<Theme>(useUiStore.getState().theme);
  const reducedRef = useRef<boolean>(useUiStore.getState().reducedMotion);
  // calm mode: when ambient audio is on, motion amplitude halves (plan §6)
  const audioCalmRef = useRef<boolean>(useUiStore.getState().audioEnabled);

  useEffect(() => {
    const unsub = useUiStore.subscribe((s) => {
      themeRef.current = s.theme;
      reducedRef.current = s.reducedMotion;
      // sound toggle doubles as "calm mode" — halves motion amplitude (plan §6)
      audioCalmRef.current = s.audioEnabled;
    });
    return unsub;
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current!;
    const ctx = canvas?.getContext('2d', { alpha: true })!;
    // runtime guard; the `!` above keeps the non-null type inside the closures
    if (!canvas || !ctx) return;

    let w = 0;
    let h = 0;
    let dpr = Math.min(window.devicePixelRatio || 1, DPR_MAX);

    const mapPoints: MapPoint[] = sampleSaudiPoints(94);
    let particles: Particle[] = [];
    const ripples: Ripple[] = [];
    const pointer = { x: -9999, y: -9999, active: false };

    // map placement (recomputed on resize)
    let mapCx = 0;
    let mapCy = 0;
    let mapW = 0;
    let mapH = 0;

    function layout() {
      const rect = canvas.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      dpr = Math.min(window.devicePixelRatio || 1, DPR_MAX);
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const wide = w > 900;
      // Saudi normalized box is ~1.27:1 (w:h). Size the map to read as the hero centerpiece.
      mapW = Math.min(w * (wide ? 0.62 : 0.92), 900);
      mapH = mapW * 0.79;
      mapCx = w * (wide ? 0.58 : 0.5);
      mapCy = h * (wide ? 0.46 : 0.36);

      // fewer free particles so the map dominates (plan §9 floor still respected)
      const target = Math.min(120, Math.round((w * h) / 17000));
      if (particles.length !== target) {
        particles = Array.from({ length: target }, () => spawn());
      }
    }

    function spawn(): Particle {
      return {
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.25,
        vy: (Math.random() - 0.5) * 0.25,
        r: Math.random() * 1.6 + 0.6,
        hue: Math.random() > 0.7 ? 1 : 0,
        a: Math.random() * 0.5 + 0.2,
      };
    }

    // map normalized point -> screen coords
    function mapToScreen(p: MapPoint): [number, number] {
      return [mapCx + (p.x - 0.5) * mapW, mapCy + (p.y - 0.5) * mapH];
    }

    /* ---------- interaction ---------- */
    function onMove(e: PointerEvent) {
      const rect = canvas.getBoundingClientRect();
      pointer.x = e.clientX - rect.left;
      pointer.y = e.clientY - rect.top;
      pointer.active = true;
    }
    function onLeave() {
      pointer.active = false;
      pointer.x = -9999;
      pointer.y = -9999;
    }
    function onDown(e: PointerEvent) {
      const rect = canvas.getBoundingClientRect();
      ripples.push({ x: e.clientX - rect.left, y: e.clientY - rect.top, t: 0 });
      if (ripples.length > 6) ripples.shift();
    }

    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('pointerdown', onDown, { passive: true });
    window.addEventListener('pointerleave', onLeave);

    /* ---------- draw ---------- */
    let raf = 0;
    let t = 0;
    let running = true;

    function rgba(c: [number, number, number], a: number) {
      return `rgba(${c[0]},${c[1]},${c[2]},${a})`;
    }

    function render() {
      const pal = PALETTES[themeRef.current];
      const calm = audioCalmRef.current ? 0.5 : 1;
      t += 0.006 * calm;

      ctx.clearRect(0, 0, w, h);

      // 1 — ambient glows: rose skyline (bottom-right) + mint halo behind map
      const g1 = ctx.createRadialGradient(w * 0.82, h * 0.92, 0, w * 0.82, h * 0.92, Math.max(w, h) * 0.55);
      g1.addColorStop(0, rgba(pal.glow, themeRef.current === 'dark' ? 0.16 : 0.08));
      g1.addColorStop(1, rgba(pal.glow, 0));
      ctx.fillStyle = g1;
      ctx.fillRect(0, 0, w, h);

      const g2 = ctx.createRadialGradient(mapCx, mapCy, 0, mapCx, mapCy, mapW * 0.75);
      g2.addColorStop(0, rgba(pal.terrain, themeRef.current === 'dark' ? 0.08 : 0.05));
      g2.addColorStop(1, rgba(pal.terrain, 0));
      ctx.fillStyle = g2;
      ctx.fillRect(0, 0, w, h);

      // 2 — orbital globe arcs behind the map
      ctx.save();
      ctx.translate(mapCx, mapCy);
      for (let i = 0; i < 3; i++) {
        const rot = t * (0.12 + i * 0.05) + (i * Math.PI) / 3;
        const rx = mapW * (0.62 + i * 0.12);
        const ry = mapH * (0.52 + i * 0.1);
        ctx.save();
        ctx.rotate(rot);
        ctx.beginPath();
        ctx.ellipse(0, 0, rx, ry, 0, 0, Math.PI * 2);
        ctx.strokeStyle = rgba(pal.arc, themeRef.current === 'dark' ? 0.1 : 0.07);
        ctx.lineWidth = 1;
        ctx.stroke();
        // a travelling node on each arc
        const na = t * (0.6 + i * 0.2);
        ctx.beginPath();
        ctx.arc(Math.cos(na) * rx, Math.sin(na) * ry, 2, 0, Math.PI * 2);
        ctx.fillStyle = rgba(pal.arc, 0.7);
        ctx.fill();
        ctx.restore();
      }
      ctx.restore();

      // 3a — map outline: stroke the SA border so the shape reads instantly
      const isDark = themeRef.current === 'dark';
      ctx.beginPath();
      for (let i = 0; i < SAUDI_POLYGON.length; i++) {
        const [nx, ny] = SAUDI_POLYGON[i];
        const px = mapCx + (nx - 0.5) * mapW;
        const py = mapCy + (ny - 0.5) * mapH;
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.closePath();
      // soft inner glow fill
      ctx.fillStyle = rgba(pal.mapDot, isDark ? 0.05 : 0.04);
      ctx.fill();
      // glowing border (subtler on paper so it never fights body text)
      ctx.save();
      ctx.shadowColor = rgba(pal.mapDot, isDark ? 0.9 : 0.4);
      ctx.shadowBlur = isDark ? 12 : 6;
      ctx.strokeStyle = rgba(pal.mapDot, isDark ? 0.5 : 0.3);
      ctx.lineWidth = 1.4;
      ctx.lineJoin = 'round';
      ctx.stroke();
      ctx.restore();

      // 3b — dotted Saudi map (with pointer repulsion + twinkle)
      for (let i = 0; i < mapPoints.length; i++) {
        const p = mapPoints[i];
        let [sx, sy] = mapToScreen(p);

        if (pointer.active) {
          const dx = sx - pointer.x;
          const dy = sy - pointer.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < 16000) {
            const d = Math.sqrt(d2) || 1;
            const f = (1 - d / 126.5) * 20;
            sx += (dx / d) * f;
            sy += (dy / d) * f;
          }
        }

        const tw = 0.6 + 0.4 * Math.sin(t * 2 + p.phase);
        // brighter dots on ink; softer on paper to keep text legible
        const base = (isDark ? 0.42 : 0.26) + p.edge * (isDark ? 0.5 : 0.34);
        ctx.fillStyle = rgba(pal.mapDot, base * tw);
        const s = 1.2 + p.edge * 1.3;
        ctx.fillRect(sx - s / 2, sy - s / 2, s, s);
      }

      // 4 — free-floating particles (pointer repulsion + connective glow)
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        p.x += p.vx * calm;
        p.y += p.vy * calm;

        if (pointer.active) {
          const dx = p.x - pointer.x;
          const dy = p.y - pointer.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < 14000) {
            const d = Math.sqrt(d2) || 1;
            const f = (1 - d / 118.3) * 0.6;
            p.vx += (dx / d) * f * 0.12;
            p.vy += (dy / d) * f * 0.12;
          }
        }
        // gentle damping + wrap
        p.vx *= 0.99;
        p.vy *= 0.99;
        if (p.x < -10) p.x = w + 10;
        if (p.x > w + 10) p.x = -10;
        if (p.y < -10) p.y = h + 10;
        if (p.y > h + 10) p.y = -10;

        const col = p.hue === 1 ? pal.particleB : pal.particleA;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = rgba(col, p.a);
        ctx.fill();
      }

      // 5 — particle terrain waves along the bottom
      const rows = 3;
      for (let r = 0; r < rows; r++) {
        const baseY = h * (0.82 + r * 0.06);
        const amp = 26 - r * 6;
        const cols = Math.ceil(w / 26);
        for (let c = 0; c <= cols; c++) {
          const x = c * 26;
          const y = baseY + Math.sin(x * 0.012 + t * 1.4 + r) * amp * calm;
          const a = (0.5 - r * 0.12) * (0.5 + 0.5 * Math.sin(x * 0.02 + t));
          ctx.fillStyle = rgba(r === 0 ? pal.terrain : pal.glow, Math.max(0, a) * 0.5);
          ctx.fillRect(x, y, 1.6, 1.6);
        }
      }

      // 6 — click ripples
      for (let i = ripples.length - 1; i >= 0; i--) {
        const rp = ripples[i];
        rp.t += 0.02;
        if (rp.t >= 1) {
          ripples.splice(i, 1);
          continue;
        }
        const rad = rp.t * 180;
        ctx.beginPath();
        ctx.arc(rp.x, rp.y, rad, 0, Math.PI * 2);
        ctx.strokeStyle = rgba(pal.glow, (1 - rp.t) * 0.4);
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }
    }

    function loop() {
      if (!running) return;
      render();
      raf = requestAnimationFrame(loop);
    }

    function drawStatic() {
      // reduced-motion: single frozen frame — map + glow, no animation (plan §6)
      const pal = PALETTES[themeRef.current];
      ctx.clearRect(0, 0, w, h);
      const g1 = ctx.createRadialGradient(w * 0.82, h * 0.92, 0, w * 0.82, h * 0.92, Math.max(w, h) * 0.55);
      g1.addColorStop(0, rgba(pal.glow, 0.12));
      g1.addColorStop(1, rgba(pal.glow, 0));
      ctx.fillStyle = g1;
      ctx.fillRect(0, 0, w, h);
      for (const p of mapPoints) {
        const [sx, sy] = mapToScreen(p);
        ctx.fillStyle = rgba(pal.mapDot, 0.3 + p.edge * 0.4);
        const s = 1 + p.edge;
        ctx.fillRect(sx - s / 2, sy - s / 2, s, s);
      }
    }

    function start() {
      layout();
      if (reducedRef.current) {
        drawStatic();
      } else {
        running = true;
        render(); // synchronous first frame — no blank flash before rAF
        raf = requestAnimationFrame(loop);
      }
    }
    function stop() {
      running = false;
      cancelAnimationFrame(raf);
    }

    // pause when tab hidden (plan §6 perf rules)
    function onVis() {
      if (document.hidden) stop();
      else start();
    }
    document.addEventListener('visibilitychange', onVis);

    const ro = new ResizeObserver(() => {
      const wasRunning = running;
      stop();
      layout();
      if (reducedRef.current) drawStatic();
      else if (wasRunning || !document.hidden) {
        running = true;
        render();
        raf = requestAnimationFrame(loop);
      }
    });
    ro.observe(canvas);

    start();

    return () => {
      stop();
      ro.disconnect();
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerdown', onDown);
      window.removeEventListener('pointerleave', onLeave);
    };
  }, []);

  return <canvas ref={canvasRef} className="value-field" aria-hidden="true" />;
}
